"use client";

// AnalyticsIdentity — ties PostHog events to the signed-in person (their user id,
// with email as a property) once they have a session, and clears that identity on
// sign-out so the next person on a shared device isn't stitched to them.
//
// Anonymous visitors (the whole public landing) are NEVER identified — this only
// fires when Supabase reports a real session. Everything no-ops when analytics or
// Supabase env is absent, so it's safe on every surface incl. dev/preview.

import { useEffect } from "react";
import { createClient } from "../lib/supabase/client";
import { isAppPath } from "../lib/subdomain";
import { identify, resetAnalytics, track } from "../lib/analytics";
import { X_EVENTS, xConvert } from "../lib/xads";
import { captureFirstTouch, sendFirstTouch } from "../lib/utm";

// The CONVERSION event — the real funnel is pageview → begin_opened → this.
// Signup happens through OAuth/magic-link, so the only reliable "it worked"
// signal is the session appearing client-side. A brand-new account (created
// within the window) fires `signup_completed`; a returning account's first
// session on this device fires `signed_in`. localStorage-guarded per user so
// neither ever double-fires and the funnel stays countable.
const NEW_ACCOUNT_WINDOW_MS = 10 * 60 * 1000;
function markConversion(user: { id: string; created_at?: string; app_metadata?: { provider?: string } }) {
  try {
    // FIRE ONLY FROM AN APP PATH — this is what keeps the count honest.
    //
    // The guard below is localStorage, and localStorage is per-ORIGIN. Signup
    // crosses two of them: the session first appears on innernet.live/auth/land
    // and again a heartbeat later on yours.innernet.live/dashboard. Each origin
    // had its own guard, so each fired, and `signup_completed` came out at 1.85
    // events per person — 100 events for 54 real accounts. The X ads conversion
    // rode along and double-counted too.
    //
    // App routes live on exactly ONE origin (the dashboard host — the same rule
    // proxy.ts routes by), so gating on `isAppPath` means the conversion has
    // exactly one place it can fire, and the per-origin guard is sound again.
    // /auth/land is a transient bounce, never a destination; every account that
    // reaches the product passes through an app path a moment later, and the
    // guard is only written once we actually fire, so a person who somehow
    // never arrives is still converted by their next visit rather than lost.
    // With no dashboard host configured (dev, preview) it is all one origin and
    // this changes nothing.
    if (!isAppPath(window.location.pathname)) return;
    const key = `innernet-ph-conv-${user.id}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, new Date().toISOString());
    const createdMs = user.created_at ? Date.parse(user.created_at) : NaN;
    const isNew = Number.isFinite(createdMs) && Date.now() - createdMs < NEW_ACCOUNT_WINDOW_MS;
    track(isNew ? "signup_completed" : "signed_in", {
      provider: user.app_metadata?.provider ?? "unknown",
    });
    // Mirror the SAME conversion to X so boosted posts can attribute a signup.
    // Only a genuinely new account counts — a returning sign-in is not a
    // conversion. The user id rides as conversion_id purely for dedup; no PII.
    if (isNew) xConvert(X_EVENTS.signup, user.id);
  } catch {
    /* analytics must never throw into a user flow */
  }
}

export default function AnalyticsIdentity() {
  // FIRST TOUCH — record where this visitor came from while they are still
  // anonymous, before any auth redirect rewrites the URL and loses the referrer.
  // Stored in localStorage (never a cookie), then posted once below when a
  // session appears. Without this, an account carries no memory of its origin
  // and acquisition can never be joined to what the person actually did.
  useEffect(() => {
    captureFirstTouch();
  }, []);

  useEffect(() => {
    let supabase: ReturnType<typeof createClient>;
    try {
      supabase = createClient();
    } catch {
      return; // Supabase env not configured — nothing to identify against
    }

    let current: string | null = null;
    const apply = (
      user: { id: string; email?: string | null; created_at?: string; app_metadata?: { provider?: string } } | null,
    ) => {
      if (user) {
        if (user.id !== current) {
          identify(user.id, user.email ? { email: user.email } : undefined);
          current = user.id;
          markConversion(user);
          // land the stored first touch on the account — once, ever. The route
          // takes the user id from the session, and the row is write-once, so a
          // repeat post can never rewrite where this person originally came from.
          void sendFirstTouch();
        }
      } else if (current) {
        resetAnalytics();
        current = null;
      }
    };

    supabase.auth
      .getUser()
      .then(({ data }) => apply(data.user ?? null))
      .catch(() => {});
    const { data } = supabase.auth.onAuthStateChange((_event, session) => apply(session?.user ?? null));
    return () => data.subscription.unsubscribe();
  }, []);

  return null;
}
